<template>
  <div
    :class="{ 'orders-sidebar-kiosk': isKioskMode }"
    class="orders-sidebar"
  >
    <div class="orders-head">
      <div
        :class="{ 'bg-primary': isKioskMode }"
        class="orders-top"
      >
        <div class="d-flex align-items-center">
          <div class="orders-top-num">
            <span>
              {{ $t("amountandpaymentmethod.order-no") }}
            </span>
            <strong># {{ displayOrderWithTable }}</strong>
          </div>

          <div
            v-if="isKioskMode"
            class="orders-top-num ml-auto"
          >
            <span>Order Type</span>
            <strong>{{ orderDiningOption ? orderDiningOption.name : "Undefined" }}</strong>
          </div>

          <div class="orders-top-btn ml-auto d-flex align-items-center">
            <b-button
              v-if="showEdit"
              :class="{ 'd-none': isKioskMode }"
              :to="{ name: 'Order', params: { id: (currentOrder.id || currentOrder.local_id) }}"
              variant="dark"
            >
              {{ $t("amountandpaymentmethod.edit-order") }}
            </b-button>

            <b-button
              variant="secondary"
              @click="$emit('cancel-order')"
            >
              {{ $t("orderscreen.cancel-order") }}
            </b-button>
          </div>
        </div>
      </div>
      <div
        v-if="isKioskMode"
        class="orders-list-th"
      >
        <div class="d-flex align-items-center h-100 justify-content-between">
          <div class="th">
            QTY
          </div>
          <div class="th">
            PRODUCT NAME
          </div>
          <div class="th ml-auto">
            PRICE
          </div>
        </div>
      </div>
      <div
        v-if="!isKioskMode"
        class="orders-add"
      >
        <b-button
          variant="link"
          @click="$emit('show-select-customer-modal')"
        >
          <div v-if="!currentOrder.customer">
            <svg class="icon icon-icon-add">
              <use xlink:href="#icon-icon-add" />
            </svg>

            <span>
              {{ $t("orderscreen.add-customer") }}
            </span>
          </div>
          <div v-else>
            {{ currentOrder.customer.first_name }} {{ currentOrder.customer.last_name }}
          </div>
        </b-button>

        <b-button
          v-if="showEdit && allowCashDiscount && allowCashDiscount.value === 'true'"
          :variant="currentOrder && currentOrder.cash_discount ? 'primary' : 'outline-light'"
          :disabled="currentOrder && currentOrder.payments && currentOrder.payments.length > 0"
          @click="$emit('toggle-cash-discount')"
        >
          {{ $t("orderscreen.cash-discount") }}
          {{ currentOrder && currentOrder.cash_discount ? $t("orderscreen.on") : $t("orderscreen.off") }}
        </b-button>
      </div>
    </div>

    <div class="orders-sidebar-body ">
      <div
        :class="{ 'orders-list-kiosk': isKioskMode }"
        class="orders-list"
        role="tablist"
      >
        <div
          v-if="!itemsAtCurrentOrder.length && isKioskMode"
          class="orders-empty-kiosk"
        >
          <h5>
            Your card is empty
          </h5>
          <p>
            Please, add items to get started.
          </p>

          <img
            src="@/assets/images/powered.svg"
            class="kiosk-powered"
          >
        </div>

        <div v-if="!isKioskMode">
          <b-card
            v-for="(item, index) in itemsAtCurrentOrder"
            :class="{'sent_to_kitchen': item.sent_to_kitchen, 'points-redeemed-item': item.name === 'Points Redeemed'}"
            :key="index"
            no-body
          >
            <b-card-header
              header-tag="header"
              :class="{ 'points-redeemed-head': item.name === 'Points Redeemed' }"
              class="orders-item-head p-0"
              role="tab"
            >
              <b-button
                v-b-toggle="'accordion-'+index"
                block
                href="#"
              >
                <div class="d-flex">
                  <div class="btn-num">
                    {{ item.quantity }}
                  </div>

                  <div class="btn-body">
                    <div class="d-flex align-items-center justify-content-between top">
                      <div class="text-left">
                        {{ getItemDisplayName(item) }}
                        <span
                          v-if="item.name === 'Points Redeemed'"
                          class="badge badge-rewards ml-2"
                        >★ REWARDS</span>
                        <span v-if="item.product === -2 && item.gift_card_number"> (card number: {{ item.gift_card_number }})</span>
                      </div>
                      <div class="text-right">
                        {{ currency }} {{
                          getItemGrandTotal(item) ? getItemGrandTotal(item).toFixed(2) : "0.00"
                        }}
                      </div>
                    </div>
                    <div
                      v-if="item.discount > 0 || item.wholeDiscount > 0 || item.wholeDiscount < 0"
                      class="d-flex align-items-center justify-content-between sub"
                    >
                      <div class="text-left">
                        {{ currency }} {{ $t("amountandpaymentmethod.discount") }}
                        <template v-if="item.promotion_applied">
                          <span
                            v-if="item.applied_promotions && item.applied_promotions.length > 0"
                            v-for="(promo, idx) in item.applied_promotions"
                            :key="idx"
                            class="badge badge-info ml-1"
                            style="font-size: 0.75em;"
                          >{{ promo.promotionName }}</span>
                          <span
                            v-else
                            class="badge badge-info ml-1"
                            style="font-size: 0.75em;"
                          >PROMO</span>
                        </template>
                      </div>
                      <div class="text-right">
                        {{ item.discount.toFixed(2) }}
                      </div>
                    </div>
                  </div>
                </div>
              </b-button>
            </b-card-header>
            <b-collapse
              :id="'accordion-'+index"
              accordion="my-accordion"
              role="tabpanel"
              class="sub-accordion"
            >
              <b-card-body>
                <div
                  v-if="item.discount > 0 || item.wholeDiscount > 0 || item.wholeDiscount < 0"
                  class="d-flex align-items-center justify-content-between sub-discount text-nowrap"
                >
                  <div class="text-left">
                    {{ currency }} {{ $t("orderscreen.discount") }}
                    <template v-if="item.promotion_applied">
                      <span
                        v-if="item.applied_promotions && item.applied_promotions.length > 0"
                        v-for="(promo, idx) in item.applied_promotions"
                        :key="idx"
                        class="badge badge-info ml-1"
                      >{{ promo.promotionName }}</span>
                      <span
                        v-else
                        class="badge badge-info ml-1"
                      >PROMO</span>
                    </template>
                  </div>
                  <div class="text-right">
                    {{ currency }} {{ ((item.discount || 0) * item.quantity).toFixed(2) }}
                  </div>
                </div>
                <div
                  v-if="item.applied_promotions && item.applied_promotions.length > 0"
                  class="d-flex align-items-center justify-content-between sub-promotion text-nowrap"
                >
                  <div class="text-left">
                    <small class="text-muted">
                      <span
                        v-for="(promo, idx) in item.applied_promotions"
                        :key="idx"
                      >
                        {{ promo.promotionName }}
                        <span v-if="idx < item.applied_promotions.length - 1">, </span>
                      </span>
                    </small>
                  </div>
                </div>
                <div
                  v-for="modifierGroup in getModifiersByItemPosition(item.id)"
                  :key="modifierGroup.id"
                  class="mod"
                >
                  <div class="d-flex align-items-center justify-content-between sub text-nowrap">
                    <div class="text-left mod-name">
                      {{ modifierGroup.name }}
                    </div>
                    <div class="text-right" />
                  </div>
                  <div
                    v-for="modifier of modifierGroup.modifiers"
                    :key="modifier.id"
                    class="d-flex align-items-center justify-content-between sub text-nowrap"
                  >
                    <div class="text-left">
                      {{ modifier.name }}
                    </div>
                    <div class="text-right">
                      {{ currency }} {{ (item.quantity * modifier.price).toFixed(2) }}
                    </div>
                  </div>
                </div>
              </b-card-body>
            </b-collapse>
          </b-card>
        </div>

        <div v-if="isKioskMode">
          <b-card
            v-for="item in itemsAtCurrentOrder"
            :key="item.id"
            variant="link"
            no-body
            class="list-complete-item"
            @click="$emit('change-quantity', item.id)"
          >
            <div class="card-header p-0">
              <div class="d-flex align-items-center justify-content-between">
                <b-button
                  class="card-qnt"
                  variant="link"
                >
                  <span class="bg-primary text-white">
                    {{ item.quantity }}
                  </span>
                </b-button>

                <h6 class="card-title mb-0">
                  {{ getItemDisplayName(item) }}
                  <template v-if="item.promotion_applied">
                    <span
                      v-if="item.applied_promotions && item.applied_promotions.length > 0"
                      v-for="(promo, idx) in item.applied_promotions"
                      :key="idx"
                      class="badge badge-info ml-1"
                      style="font-size: 0.75em;"
                    >{{ promo.promotionName }}</span>
                    <span
                      v-else
                      class="badge badge-info ml-1"
                      style="font-size: 0.75em;"
                    >PROMO</span>
                  </template>
                  <span v-if="item.product === -2 && item.gift_card_number"> (card number: {{ item.gift_card_number }})</span>
                </h6>

                <div class="card-price ml-auto">
                  <span
                    v-if="item.tax > 0 && item.tax_status === 0 && !isKioskMode"
                    class="short-desc"
                  >{{ $t("listvieworderdelegate.taxed") }}</span>
                  <span>
                    {{ currency }} {{ getItemPriceDisplay(item) }}
                  </span>
                </div>

                <b-button
                  variant="link"
                  class="btn-remove ml-1"
                  @click.stop.prevent="$emit('remove-item', item.id)"
                >
                  <svg class="icon icon-icon-remove">
                    <use xlink:href="#icon-icon-remove" />
                  </svg>
                </b-button>
              </div>

              <div
                v-for="modifierGroup in getModifiersByItemPosition(item.id)"
                :key="modifierGroup.id"
                class="mod"
              >
                <div class="d-flex align-items-center justify-content-between sub text-nowrap">
                  <div class="text-left mod-name">
                    {{ modifierGroup.name }}
                  </div>
                  <div class="text-right" />
                </div>
                <div
                  v-for="modifier of modifierGroup.modifiers"
                  :key="modifier.id"
                  class="d-flex align-items-center justify-content-between sub text-nowrap"
                >
                  <div class="text-left">
                    {{ modifier.name }}
                  </div>
                  <div class="text-right">
                    {{ currency }} {{ (item.quantity * modifier.price).toFixed(2) }}
                  </div>
                </div>
              </div>
            </div>
          </b-card>
        </div>

        <div v-if="itemsAtCurrentOrder.length && isKioskMode">
          <b-card
            no-body
            class="list-complete-item card-empty"
          >
            <b-button
              v-if="showEdit"
              :to="{ name: 'Order', params: { id: (currentOrder.id || currentOrder.local_id), tableID: null }}"
            >
              <span class="icon icon-plus" />
              Add additional items
            </b-button>
          </b-card>
        </div>
      </div>
    </div>

    <div class="orders-bottom">
      <div v-if="!isDisplayPaymentDetails">
        <div class="orders-bottom-item">
          <span>{{ $t("amountandpaymentmethod.total") }}</span>
          <span class="text-right">
            {{ currency }} {{ currentOrder.grand_total ? currentOrderSum.toFixed(2) : "0.00" }}
            <b-button
              variant="link"
              class="w-100"
              @click="$emit('display-payment-details')"
            >
              {{ $t("ordertotal.view-details") }}
            </b-button>
          </span>
        </div>
      </div>
      <div v-else>
        <div class="orders-bottom-item">
          <b-button
            variant="link"
            class="w-100 text-center"
            @click="$emit('display-payment-details')"
          >
            {{ $t("ordertotal.close-details") }}
          </b-button>
        </div>
        <div class="orders-bottom-item">
          <span>
            {{ $t("amountandpaymentmethod.discount") }}
            <template v-if="currentOrder.promotion_applied">
              <span
                v-if="currentOrder.applied_order_promotions && currentOrder.applied_order_promotions.length > 0"
                v-for="(promo, idx) in currentOrder.applied_order_promotions"
                :key="idx"
                class="badge badge-info ml-1"
              >{{ promo.promotionName }}</span>
              <span
                v-else
                class="badge badge-info ml-1"
              >PROMO</span>
            </template>
          </span>
          <span>{{ currency }} {{ currentOrder.discount_total.toFixed(2) }}</span>
        </div>
        <div
          v-if="currentOrder.applied_order_promotions && currentOrder.applied_order_promotions.length > 0"
          class="orders-bottom-item"
        >
          <span class="text-left">
            <small class="text-muted">
              <span
                v-for="(promo, idx) in currentOrder.applied_order_promotions"
                :key="idx"
              >
                {{ promo.promotionName }}
                <span v-if="idx < currentOrder.applied_order_promotions.length - 1">, </span>
              </span>
            </small>
          </span>
        </div>

        <div class="orders-bottom-item">
          <span>
            {{ $t("amountandpaymentmethod.sub-total") }}
          </span>
          <span>{{ currency }} {{ currentOrder.subtotal.toFixed(2) }}</span>
        </div>

        <div class="orders-bottom-item">
          <span>
            {{ $t("amountandpaymentmethod.tax") }}</span>
          <span>{{ currency }} {{ currentOrder ? currentOrder.tax_total.toFixed(2) : "0.00" }}</span>
        </div>

        <div
          class="orders-bottom-item"
          v-if="currentOrder.tips_total"
        >
          <span>
            {{ $t("ordertotal.tip") }}
          </span>
          <span>
            {{ currency }} {{ currentOrder.tips_total.toFixed(2) }}
          </span>
        </div>

        <div
          class="orders-bottom-item"
          v-if="currentOrder.surcharge_value > 0"
        >
          <span>Non-Cash Fee</span>
          <span>{{ currency }} {{ parseFloat(currentOrder.surcharge_value).toFixed(2) }}</span>
        </div>

        <div class="orders-bottom-item">
          <span>{{ $t("amountandpaymentmethod.total") }}</span>
          <span>{{ currency }} {{ currentOrder.grand_total ? currentOrderSum.toFixed(2) : "0.00" }}
          </span>
        </div>
        <div
          v-for="(payment, index) in currentOrder.payments"
          :key="index"
          class="orders-bottom-item"
        >
          {{ $t("ordertotal.paid") }} - {{ payment.payment_type }}
          <span>
            {{ currency }} {{ payment.amount.toFixed(2) }}
            <span :style="{color: 'red'}">
              {{ `(${payment.change_amount.toFixed(2)})` }}
            </span>
          </span>
        </div>
      </div>
      <div class="orders-bottom-options">
        <div class="pay pay-total">
          <div class="d-flex align-items-center justify-content-between">
            <span
              v-if="!isKioskMode"
              class="text-left"
            >
              {{ $t("amountandpaymentmethod.balance") }}
            </span>
            <span
              v-if="isKioskMode"
              class="text-left"
            >
              {{ $t("amountandpaymentmethod.total") }}
            </span>
            <span class="text-right">
              {{ currency }} {{ currentOrderGrandTotalSum.toFixed(2) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TYPE_KITCHENHUB_ORDER } from "@/helpers/order/kitchenHubOrderMapper";

export default {
  name: "CheckoutOrderSidebar",
  props: {
    isKioskMode: {
      type: Boolean,
      default: false,
    },
    displayOrderWithTable: {
      type: [String, Number],
      default: "",
    },
    orderDiningOption: {
      type: Object,
      default: null,
    },
    // orderMixin `allowCashDiscount` db_setting ({ value: 'true' | 'false' }).
    // When enabled, a Cash Discount ON/OFF toggle is shown next to Add Customer.
    allowCashDiscount: {
      type: Object,
      default: null,
    },
    // Live store-backed order (the single-source-of-truth proxy value). Read
    // only here; all mutations are emitted up to the parent.
    currentOrder: {
      type: Object,
      default: () => ({ items: [], payments: [] }),
    },
    itemsAtCurrentOrder: {
      type: Array,
      default: () => [],
    },
    currency: {
      type: String,
      default: "",
    },
    isDisplayPaymentDetails: {
      type: Boolean,
      default: false,
    },
    currentOrderSum: {
      type: Number,
      default: 0,
    },
    currentOrderGrandTotalSum: {
      type: Number,
      default: 0,
    },
    // Parent-bound (orderMixin / orderModifierMixin) helpers.
    getItemDisplayName: {
      type: Function,
      default: (item) => (item ? item.name : ""),
    },
    getItemGrandTotal: {
      type: Function,
      default: () => 0,
    },
    getItemPriceDisplay: {
      type: Function,
      default: () => "0.00",
    },
    getModifiersByItemPosition: {
      type: Function,
      default: () => [],
    },
  },
  computed: {
    // KitchenHub orders are priced/settled on the delivery platform's side —
    // adding/removing items or changing quantities here would desync from
    // what was actually charged, so hide the way back into the cart editor
    // from checkout too.
    showEdit () {
      return !!(this.currentOrder && (this.currentOrder.id || this.currentOrder.local_id))
        && this.currentOrder.type !== TYPE_KITCHENHUB_ORDER;
    },
  },
};
</script>
